import React, { useState, useEffect } from 'react';
import { 
  Filter, X, ChevronDown, ChevronUp, RotateCcw,
  CheckSquare, Flag, User, Calendar, FolderKanban,
  GitBranch, ListTree, AlertTriangle, Shield, Layers
} from 'lucide-react';
import MultiSelectFilter from './MultiSelectFilter';
import DateRangeFilter from './DateRangeFilter';
import TrackingInsightsBar from './TrackingInsightsBar';
import PresetManager from './PresetManager';
import { api } from '../../api/client';

/**
 * TrackingFilterBar - Filter bar for tracking pages (Issues, Dependencies, Decisions)
 * Supports multi-select filters, date ranges, and quick filters via TrackingInsightsBar
 */
const TrackingFilterBar = ({
  trackingType = 'issues', // 'issues' | 'dependencies' | 'decisions'
  filters = {},
  onFiltersChange,
  quickFilters = {},
  onQuickFiltersChange,
  counts = {},
  currentUser = null,
  showInsightsBar = true,
  className = '',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    portfolios: [],
    programs: [],
    projects: [],
    owners: [],
    statuses: [],
    severities: [],
    priorities: [],
    // Issues specific
    issue_types: [],
    escalation_levels: [],
    // Dependencies specific
    dep_types: [],
    // Decisions specific
    impacts: [],
    categories: [],
    approvers: [],
  });
  const [loading, setLoading] = useState(true);

  // Load filter options on mount
  useEffect(() => {
    loadFilterOptions();
  }, [trackingType]);

  // Reload options when hierarchy filters change
  useEffect(() => {
    if (filters.portfolio?.length > 0 || filters.program?.length > 0) {
      loadFilterOptions();
    }
  }, [filters.portfolio, filters.program]);

  const loadFilterOptions = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.portfolio?.length > 0) params.portfolio_id = filters.portfolio[0];
      if (filters.program?.length > 0) params.program_id = filters.program[0];
      if (filters.project?.length > 0) params.project_id = filters.project[0];

      let res;
      switch (trackingType) {
        case 'issues':
          res = await api.getIssuesFilterOptions(params);
          setFilterOptions({
            portfolios: res.data.portfolios || [],
            programs: res.data.programs || [],
            projects: res.data.projects || [],
            owners: res.data.owners?.map(o => ({ id: o, name: o })) || [],
            statuses: res.data.statuses?.map(s => ({ id: s, name: s })) || [],
            severities: res.data.severities?.map(s => ({ id: s, name: s })) || [],
            priorities: res.data.priorities?.map(p => ({ id: p, name: p })) || [],
            issue_types: res.data.issue_types?.map(t => ({ id: t, name: t })) || [],
            escalation_levels: res.data.escalation_levels?.map(e => ({ id: e, name: e })) || [],
          });
          break;
        
        case 'dependencies':
          res = await api.getDependenciesFilterOptions(params);
          setFilterOptions({
            portfolios: res.data.portfolios || [],
            programs: res.data.programs || [],
            projects: res.data.projects || [],
            owners: res.data.owners?.map(o => ({ id: o, name: o })) || [],
            statuses: res.data.statuses?.map(s => ({ id: s, name: s })) || [],
            severities: res.data.severities?.map(s => ({ id: s, name: s })) || [],
            dep_types: res.data.dep_types?.map(t => ({ id: t, name: t })) || [],
          });
          break;
        
        case 'decisions':
          res = await api.getDecisionsFilterOptions(params);
          setFilterOptions({
            portfolios: res.data.portfolios || [],
            programs: res.data.programs || [],
            projects: res.data.projects || [],
            owners: res.data.owners?.map(o => ({ id: o, name: o })) || [],
            approvers: res.data.approvers?.map(a => ({ id: a, name: a })) || [],
            statuses: res.data.statuses?.map(s => ({ id: s, name: s })) || [],
            impacts: res.data.impacts?.map(i => ({ id: i, name: i })) || [],
            categories: res.data.categories?.map(c => ({ id: c, name: c })) || [],
            escalation_levels: res.data.escalation_levels?.map(e => ({ id: e, name: e })) || [],
          });
          break;
      }
    } catch (error) {
      console.error('Failed to load filter options:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key, value) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const clearAllFilters = () => {
    onFiltersChange({});
    if (onQuickFiltersChange) {
      onQuickFiltersChange({});
    }
  };

  // Count active filters
  const activeFilterCount = Object.entries(filters)
    .filter(([_, v]) => Array.isArray(v) ? v.length > 0 : Boolean(v))
    .length;

  const hasActiveFilters = activeFilterCount > 0 || 
    Object.values(quickFilters).some(v => v === true);

  // Get tracking type label
  const getTypeLabel = () => {
    switch (trackingType) {
      case 'issues': return 'Issues & Risks';
      case 'dependencies': return 'Dependencies';
      case 'decisions': return 'Decisions';
      default: return 'Items';
    }
  };

  // Render filters based on tracking type
  const renderTypeSpecificFilters = () => {
    switch (trackingType) {
      case 'issues':
        return (
          <>
            {/* Status */}
            <MultiSelectFilter
              label="Status"
              icon={CheckSquare}
              value={filters.status || []}
              options={filterOptions.statuses}
              onChange={(v) => updateFilter('status', v)}
              placeholder="All Statuses"
              testId="filter-status"
            />

            {/* Severity */}
            <MultiSelectFilter
              label="Severity"
              icon={AlertTriangle}
              value={filters.severity || []}
              options={filterOptions.severities}
              onChange={(v) => updateFilter('severity', v)}
              placeholder="All Severities"
              testId="filter-severity"
            />

            {/* Priority */}
            <MultiSelectFilter
              label="Priority"
              icon={Flag}
              value={filters.priority || []}
              options={filterOptions.priorities}
              onChange={(v) => updateFilter('priority', v)}
              placeholder="All Priorities"
              testId="filter-priority"
            />

            {/* Type (Issue/Risk) */}
            <MultiSelectFilter
              label="Type"
              icon={Layers}
              value={filters.issue_type || []}
              options={filterOptions.issue_types}
              onChange={(v) => updateFilter('issue_type', v)}
              placeholder="All Types"
              testId="filter-type"
            />

            {/* Escalation Level */}
            <MultiSelectFilter
              label="Escalation"
              icon={Shield}
              value={filters.escalation_level || []}
              options={filterOptions.escalation_levels}
              onChange={(v) => updateFilter('escalation_level', v)}
              placeholder="All Levels"
              testId="filter-escalation"
            />

            {/* Owner */}
            <MultiSelectFilter
              label="Owner"
              icon={User}
              value={filters.owner || []}
              options={filterOptions.owners}
              onChange={(v) => updateFilter('owner', v)}
              placeholder="All Owners"
              testId="filter-owner"
            />
          </>
        );

      case 'dependencies':
        return (
          <>
            {/* Status */}
            <MultiSelectFilter
              label="Status"
              icon={CheckSquare}
              value={filters.status || []}
              options={filterOptions.statuses}
              onChange={(v) => updateFilter('status', v)}
              placeholder="All Statuses"
              testId="filter-status"
            />

            {/* Severity */}
            <MultiSelectFilter
              label="Severity"
              icon={AlertTriangle}
              value={filters.severity || []}
              options={filterOptions.severities}
              onChange={(v) => updateFilter('severity', v)}
              placeholder="All Severities"
              testId="filter-severity"
            />

            {/* Dependency Type */}
            <MultiSelectFilter
              label="Type"
              icon={GitBranch}
              value={filters.dep_type || []}
              options={filterOptions.dep_types}
              onChange={(v) => updateFilter('dep_type', v)}
              placeholder="All Types"
              testId="filter-dep-type"
            />

            {/* Owner */}
            <MultiSelectFilter
              label="Owner"
              icon={User}
              value={filters.owner || []}
              options={filterOptions.owners}
              onChange={(v) => updateFilter('owner', v)}
              placeholder="All Owners"
              testId="filter-owner"
            />
          </>
        );

      case 'decisions':
        return (
          <>
            {/* Status */}
            <MultiSelectFilter
              label="Status"
              icon={CheckSquare}
              value={filters.status || []}
              options={filterOptions.statuses}
              onChange={(v) => updateFilter('status', v)}
              placeholder="All Statuses"
              testId="filter-status"
            />

            {/* Impact */}
            <MultiSelectFilter
              label="Impact"
              icon={AlertTriangle}
              value={filters.impact || []}
              options={filterOptions.impacts}
              onChange={(v) => updateFilter('impact', v)}
              placeholder="All Impacts"
              testId="filter-impact"
            />

            {/* Category */}
            <MultiSelectFilter
              label="Category"
              icon={Layers}
              value={filters.category || []}
              options={filterOptions.categories}
              onChange={(v) => updateFilter('category', v)}
              placeholder="All Categories"
              testId="filter-category"
            />

            {/* Escalation Level */}
            <MultiSelectFilter
              label="Escalation"
              icon={Shield}
              value={filters.escalation_level || []}
              options={filterOptions.escalation_levels}
              onChange={(v) => updateFilter('escalation_level', v)}
              placeholder="All Levels"
              testId="filter-escalation"
            />

            {/* Owner/Requestor */}
            <MultiSelectFilter
              label="Owner"
              icon={User}
              value={filters.owner || []}
              options={filterOptions.owners}
              onChange={(v) => updateFilter('owner', v)}
              placeholder="All Owners"
              testId="filter-owner"
            />

            {/* Approver */}
            <MultiSelectFilter
              label="Approver"
              icon={User}
              value={filters.approver || []}
              options={filterOptions.approvers}
              onChange={(v) => updateFilter('approver', v)}
              placeholder="All Approvers"
              testId="filter-approver"
            />
          </>
        );

      default:
        return null;
    }
  };

  // Handle loading a preset
  const handleLoadPreset = (preset) => {
    onFiltersChange(preset.filters || {});
    if (onQuickFiltersChange) {
      onQuickFiltersChange(preset.quickFilters || {});
    }
  };

  return (
    <div className={`bg-white border border-gray-200 rounded-lg ${className}`} data-testid="tracking-filter-bar">
      {/* Collapsed Header */}
      <div className="px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-2 text-sm font-medium text-[var(--clr-text-primary)] hover:text-[var(--clr-brand-primary)]"
            data-testid="tracking-filter-bar-toggle"
          >
            <Filter className="w-4 h-4" />
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <span className="px-1.5 py-0.5 bg-[var(--clr-brand-primary)] text-white text-xs rounded-full">
                {activeFilterCount}
              </span>
            )}
            {isExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>

          {/* Preset Manager */}
          <PresetManager
            pageType={trackingType}
            currentFilters={filters}
            currentQuickFilters={quickFilters}
            onLoadPreset={handleLoadPreset}
          />
        </div>

        {/* Quick filter pills when collapsed */}
        {!isExpanded && hasActiveFilters && (
          <div className="flex items-center gap-2 flex-wrap">
            {filters.status?.length > 0 && (
              <span className="px-2 py-1 bg-[var(--clr-brand-primary)] bg-opacity-10 text-[var(--clr-brand-primary)] text-xs rounded-full flex items-center gap-1">
                <CheckSquare className="w-3 h-3" />
                {filters.status.length} status{filters.status.length > 1 ? 'es' : ''}
                <button onClick={() => updateFilter('status', [])} className="hover:opacity-70">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {filters.severity?.length > 0 && (
              <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {filters.severity.length} severit{filters.severity.length > 1 ? 'ies' : 'y'}
                <button onClick={() => updateFilter('severity', [])} className="hover:opacity-70">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {filters.owner?.length > 0 && (
              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
                <User className="w-3 h-3" />
                {filters.owner.length} owner{filters.owner.length > 1 ? 's' : ''}
                <button onClick={() => updateFilter('owner', [])} className="hover:opacity-70">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            <button
              onClick={clearAllFilters}
              className="text-xs text-[var(--clr-text-muted)] hover:text-[var(--clr-sev-blocked)] flex items-center gap-1"
            >
              <RotateCcw className="w-3 h-3" />
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Expanded Filters */}
      {isExpanded && (
        <div className="border-t border-gray-100 p-4">
          {/* Main Filters Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {renderTypeSpecificFilters()}
          </div>

          {/* Date Range Filters - Second Row */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="text-xs font-medium text-[var(--clr-text-muted)] uppercase mb-3">
              Date Filters
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <DateRangeFilter
                label="Due Date"
                startDate={filters.due_date_start}
                endDate={filters.due_date_end}
                onStartDateChange={(v) => updateFilter('due_date_start', v)}
                onEndDateChange={(v) => updateFilter('due_date_end', v)}
                testId="filter-due-date"
              />
              <DateRangeFilter
                label={trackingType === 'decisions' ? 'Request Date' : 'Raised Date'}
                startDate={trackingType === 'decisions' ? filters.request_date_start : filters.raised_date_start}
                endDate={trackingType === 'decisions' ? filters.request_date_end : filters.raised_date_end}
                onStartDateChange={(v) => updateFilter(trackingType === 'decisions' ? 'request_date_start' : 'raised_date_start', v)}
                onEndDateChange={(v) => updateFilter(trackingType === 'decisions' ? 'request_date_end' : 'raised_date_end', v)}
                testId="filter-raised-date"
              />
            </div>
          </div>

          {/* Hierarchy Filters - Third Row */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="text-xs font-medium text-[var(--clr-text-muted)] uppercase mb-3">
              Hierarchy Filters
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <MultiSelectFilter
                label="Portfolio"
                icon={FolderKanban}
                value={filters.portfolio || []}
                options={filterOptions.portfolios}
                onChange={(v) => updateFilter('portfolio', v)}
                placeholder="All Portfolios"
                testId="filter-portfolio"
              />

              <MultiSelectFilter
                label="Program"
                icon={GitBranch}
                value={filters.program || []}
                options={filterOptions.programs}
                onChange={(v) => updateFilter('program', v)}
                placeholder="All Programs"
                testId="filter-program"
              />

              <MultiSelectFilter
                label="Project"
                icon={ListTree}
                value={filters.project || []}
                options={filterOptions.projects}
                onChange={(v) => updateFilter('project', v)}
                placeholder="All Projects"
                testId="filter-project"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
            <span className="text-xs text-[var(--clr-text-muted)]">
              {activeFilterCount > 0 
                ? `${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''} applied`
                : 'No filters applied'
              }
            </span>
            <button
              onClick={clearAllFilters}
              disabled={!hasActiveFilters}
              className={`px-3 py-1.5 text-sm rounded-lg flex items-center gap-1 transition-colors ${
                hasActiveFilters 
                  ? 'text-[var(--clr-text-secondary)] hover:text-[var(--clr-sev-blocked)] hover:bg-red-50' 
                  : 'text-gray-300 cursor-not-allowed'
              }`}
              data-testid="clear-all-filters"
            >
              <RotateCcw className="w-4 h-4" />
              Clear All Filters
            </button>
          </div>
        </div>
      )}

      {/* Insights Bar (Quick Filters) */}
      {showInsightsBar && (
        <div className="border-t border-gray-100">
          <TrackingInsightsBar
            trackingType={trackingType}
            filters={quickFilters}
            onFilterChange={onQuickFiltersChange}
            counts={counts}
            currentUser={currentUser}
          />
        </div>
      )}
    </div>
  );
};

export default TrackingFilterBar;
